import { InputType } from 'config'
import { accountTypes } from 'config/user.type.constants'
import { currentProductTypeOptionsMap } from 'hooks/useProductTypeOptions'
import { NotePageTypeOptions } from 'pages/Loan/Servicing/Notes/types'
import { requestedToParties } from 'pages/LoanSubmission/constants'

import { availabilityInputs } from './useTemplateAvailability'

export const condConstant = {
  respon: [
    'Closer',
    'COC',
    'Escrow Company',
    'Funder',
    'Loan Officer - Third Party',
    'Loan Processor - Third Party',
    'Loan Officer - Retail',
    'Loan Processor - Retail',
    'Post Closer',
    'Quality Control',
    'Settlement Company',
    'Title Company',
    'Underwriter',
    'Loan Setup',
  ].sort(),
  ines: ['Yes', 'No'],
}

export const taskPriority: Record<string, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

export const taskSecurity: Record<string, string> = {
  high: 'High - Only Task Creator can Modify',
  medium: 'Medium - Assignee can Modify due Date',
  low: 'Low - Anyone can Modify',
}

export const conditionInputs = (categories: string[]): Record<string, InputType> => {
  return {
    displayNo: {
      inputType: 'text',
      type: 'number',
      title: 'No',
      required: false,
      key: 'displayNo',
    },
    name: {
      inputType: 'text',
      title: 'Name',
      value: '',
      error: '',
      required: true,
      key: 'name',
    },
    category: {
      title: 'Category',
      inputType: 'Select',
      options: categories,
      value: '',
      error: '',
      key: 'category',
      required: true,
      hasDefaultOption: true,
    },
    type: {
      title: 'Type',
      inputType: 'Select',
      options: [],
      value: '',
      error: '',
      key: 'type',
      required: true,
      hasDefaultOption: true,
    },
    responsibility: {
      title: 'Responsibility',
      inputType: 'Select',
      options: Object.values(accountTypes),
      value: '',
      error: '',
      key: 'responsibility',
      required: true,
      hasDefaultOption: true,
      sort: true,
    },
    intext: {
      title: 'Internal',
      inputType: 'Check',
      value: false,
      error: '',
      key: 'intext',
      required: true,
    },
    documentCategory: {
      title: 'Document Category',
      inputType: 'Select',
      options: [],
      value: '',
      error: '',
      key: 'documentCategory',
      required: true,
      hasDefaultOption: true,
    },
    description: {
      title: 'UW Description',
      inputType: 'TextArea',
      value: '',
      error: '',
      key: 'description',
      required: true,
      rows: 6,
      length: 1,
      span: 'full',
    },
    brokerDescription: {
      title: 'Broker Description',
      inputType: 'TextArea',
      value: '',
      error: '',
      key: 'description',
      required: true,
      rows: 4,
      length: 1,
      span: 'full',
    },
    borrowerName: {
      inputType: 'text',
      title: 'Name (Borrower Facing)',
      value: '',
      error: '',
      required: false,
      key: 'borrowerName',
      span: 'full',
    },
    borrowerDescription: {
      title: 'Description (Borrower Facing)',
      inputType: 'TextArea',
      value: '',
      error: '',
      key: 'borrowerDescription',
      required: false,
      rows: 4,
      length: 1,
      span: 'full',
    },
    projectInstructionsSection: {
      inputType: 'section',
      title: 'Project Instructions',
      span: 'full',
    },
    projectInstructions: {
      title: 'Tell the AI how to review this condition. Left blank, the AI review is unchanged.',
      inputType: 'TextArea',
      value: '',
      error: '',
      key: 'projectInstructions',
      required: false,
      rows: 6,
      length: 1,
      span: 'full',
    },
    requested: {
      title: 'Requested at Application',
      inputType: 'Check',
      value: false,
      error: '',
      key: 'requested',
    },
    requestedTo: {
      title: 'Requested To',
      inputType: 'Select',
      options: requestedToParties,
      value: '',
      error: '',
      key: 'requestedTo',
      required: false,
    },
    products: {
      inputType: 'multiselect',
      title: 'Products',
      options: currentProductTypeOptionsMap(),
      key: 'products',
      defaultSelected: true,
    },
    transactions: {
      inputType: 'multiselect',
      title: 'Transactions',
      options: ['Purchase', 'Refinance'],
      key: 'transactions',
      defaultSelected: true,
    },
    reviewActionSection: {
      inputType: 'section',
      title: 'Use Loan Submission Action',
      span: 'full',
    },
    reviewActionStatus: {
      inputType: 'toggle',
      title: 'Has Loan Submission Action?',
    },
    reviewActionChains: {
      inputType: 'SelectOptionInput',
      value: [],
      title: 'Loan Submission Action Chains',
      disabled: true,
      allCanDelete: true,
      minLength: 1,
      showKey: true,
      showVisibleBtn: false,
    },
    checklistSection: {
      inputType: 'section',
      title: 'Checklist',
      span: 'full',
    },
    checklistEnabled: {
      inputType: 'check',
      title: 'Is Checklist Enabled?',
    },
    showExpirationDates: {
      inputType: 'toggle',
      title: 'Show Expiration Dates',
    },
  }
}

export const nonADHocConditionKeys = [
  'borrowerName',
  'borrowerDescription',
  'projectInstructionsSection',
  'projectInstructions',
  'requested',
  'products',
  'transactions',
  'reviewActionSection',
  'reviewActionStatus',
  'checklistSection',
  'checklistEnabled',
  'reviewActionChains',
  'showExpirationDates',
]

export const taskInputs = (): Record<string, InputType> => {
  return {
    description: {
      inputType: 'text',
      title: 'Description',
      value: '',
      error: '',
      required: true,
      key: 'description',
    },
    assignedTo: {
      inputType: 'multiselect',
      title: 'Assigned to',
      options: {},
      defaultSelected: false,
      required: true,
      value: [],
      sort: true,
    },
    assignedToSecondary: {
      inputType: 'multiselect',
      title: 'Assigned to (Secondary)',
      options: {},
      defaultSelected: false,
      value: [],
      sort: true,
    },
    assignedToManager: {
      inputType: 'multiselect',
      title: 'Assigned to Manager',
      options: {},
      defaultSelected: false,
      value: [],
      sort: true,
    },
    dueDays: {
      inputType: 'text',
      title: 'Due days',
      value: '',
      error: '',
      required: true,
      key: 'dueDays',
      // type: 'number',
    },
    replyCcUser: {
      inputType: 'multiselect',
      title: 'Reply CC User',
      options: {},
      defaultSelected: false,
      required: false,
      value: [],
    },
    notes: {
      inputType: 'text',
      title: 'Notes',
      value: '',
      error: '',
      key: 'notes',
      required: true,
    },
  }
}

export const AmountCalculationMap = {
  0: 'Wire Amount',
  1: 'Loan Amount',
}

export const warehouseLenderInputs = () => {
  return {
    code: {
      title: 'Code',
      value: '',
      error: '',
      required: true,
      key: 'code',
    },
    fullName: {
      title: 'Full Name',
      value: '',
      error: '',
      required: false,
      key: 'fullName',
    },
    fannieMaePayeeID: {
      title: 'Fannie Mae Payee ID',
      value: '',
      error: '',
      required: false,
      key: 'fannieMaePayeeID',
    },
    fannieMaeWarehouseID: {
      title: 'Fannie Mae Warehouse ID',
      value: '',
      error: '',
      required: false,
      key: 'fannieMaeWarehouseID',
    },
    freddieMacWarehouseID: {
      title: 'Freddie Mac Warehouse ID',
      value: '',
      error: '',
      required: false,
      key: 'freddieMacWarehouseID',
    },
    advancedAmount: {
      title: 'Advance Amount',
      value: '',
      error: '',
      required: false,
      key: 'advancedAmount',
      type: 'number',
    },
    amountCalculation: {
      title: 'Adv. Amt Calculation',
      inputType: 'Select',
      options: AmountCalculationMap,
      hasDefaultOption: true,
      value: '',
      error: '',
      required: true,
      key: 'amountCalculation',
    },
    lineBalance: {
      title: 'Line Balance',
      value: '',
      error: '',
      required: false,
      key: 'lineBalance',
      type: 'number',
    },
    accountNumber: {
      title: 'Account Number',
      value: '',
      error: '',
      required: false,
      key: 'accountNumber',
      type: 'number',
    },
    mersOrgID: {
      title: 'MERS Org ID',
      value: '',
      error: '',
      required: false,
      key: 'mersOrgID',
    },
    notes: {
      title: 'Notes',
      value: '',
      error: '',
      key: 'notes',
      inputType: 'textarea',
      row: 5,
      span: 2,
      required: false,
    },
  }
}

export const reminderTemplateInputs = (): Record<string, InputType> => {
  return {
    title: {
      inputType: 'text',
      title: 'Title',
      value: '',
      error: '',
      required: true,
      key: 'title',
    },
    description: {
      inputType: 'textarea',
      title: 'Description',
      value: '',
      error: '',
      key: 'description',
      required: true,
      span: 'full',
      rows: 6,
    },
  }
}

export const noteInputs = () => {
  return {
    title: {
      inputType: 'text',
      title: 'Title',
      value: '',
      error: '',
      required: true,
      key: 'title',
    },
    notes: {
      // A note template prefills the note body, which is now a rich-text
      // field rendered into a <div>, so block HTML is valid here.
      inputType: 'richtext',
      variant: 'block',
      title: 'Notes',
      value: '',
      error: '',
      key: 'notes',
      required: true,
      minHeight: 260,
      span: 'full',
    },
    notePageTypes: {
      inputType: 'multiselect',
      title: 'Available In',
      options: NotePageTypeOptions,
      value: [],
      error: '',
      key: 'notePageTypes',
      required: false,
      span: 'full',
      tooltip: 'Leave empty to offer this template in every notes list.',
    },
    ...availabilityInputs(),
  }
}

export const uwCategoryInputs = (loanStatusObj: Record<string, string> | null = null): Record<string, InputType> => {
  const data: Record<string, InputType> = {
    title: {
      inputType: 'text',
      title: 'Title',
      value: '',
      error: '',
      required: true,
      key: 'title',
    },
    notes: {
      inputType: 'textarea',
      title: 'Description',
      value: '',
      error: '',
      key: 'notes',
      required: false,
      span: 'full',
    },
    loanStatus: {
      inputType: 'multiselect',
      title: 'Loan Status for Cleared Condition in Pipeline',
      value: [],
      error: '',
      required: false,
      options: loanStatusObj || {},
      defaultSelected: false,
    },
    addToLoanStatusRestrictions: {
      inputType: 'toggle',
      title: 'Add to Loan Status Restrictions',
      value: false,
    },
  }

  if (!loanStatusObj) {
    delete data.loanStatus
    delete data.addToLoanStatusRestrictions
  }

  return data
}
